import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Badge, List, Avatar } from 'antd';
import {
  UserOutlined,
  ShoppingOutlined,
  DollarOutlined,
  FileTextOutlined,
  CarOutlined,
  SafetyCertificateOutlined,
  RiseOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { db } from '../../services/firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { CARD_STYLE } from '../../constants/theme';

// ── Stat Card Component ──
const StatCard = ({ title, value, prefix, suffix, icon, color, trend }) => (
  <Card style={{ ...CARD_STYLE, height: '100%' }} bodyStyle={{ padding: 20 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ fontSize: 12, color: '#7A8A96', marginBottom: 8 }}>{title}</div>
        <Statistic
          value={value}
          prefix={prefix}
          suffix={suffix}
          valueStyle={{ color: color || '#C9A84C', fontSize: 28, fontWeight: 600 }}
        />
        {trend && (
          <div style={{ fontSize: 11, color: '#52C41A', marginTop: 4 }}>
            <RiseOutlined /> {trend}
          </div>
        )}
      </div>
      <div style={{
        width: 48, height: 48,
        borderRadius: 12,
        background: `${color}20` || 'rgba(201,168,76,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22,
      }}>
        {icon}
      </div>
    </div>
  </Card>
);

// ── Main Dashboard ──
export default function Dashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingKYC: 0,
    totalOrders: 0,
    totalRevenue: 0,
    activeDeliveries: 0,
    openTickets: 0,
    leadsToday: 0,
    pendingPayouts: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load admin users count
      const usersSnap = await getDocs(collection(db, 'adminUsers'));
      
      // Load audit logs for recent activity
      const auditQuery = query(
        collection(db, 'auditLogs'),
        orderBy('timestamp', 'desc'),
        limit(5)
      );

      let auditLogs = [];
      try {
        const auditSnap = await getDocs(auditQuery);
        auditLogs = auditSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          time: doc.data().timestamp?.toDate?.()?.toLocaleTimeString() || 'Just now',
        }));
      } catch (e) {
        // Audit logs collection may be empty
      }

      setStats(prev => ({
        ...prev,
        totalUsers: usersSnap.size,
      }));

      setRecentActivity(auditLogs.length > 0 ? auditLogs : [
        { id: 1, action: 'Admin Panel launched', adminEmail: 'System', time: 'Today' },
        { id: 2, action: 'Firebase connected', adminEmail: 'System', time: 'Today' },
        { id: 3, action: 'Security rules set', adminEmail: 'System', time: 'Today' },
      ]);

    } catch (error) {
      console.error('Dashboard load error:', error);
    }
    setLoading(false);
  };

  // Quick action cards
  const quickActions = [
    { title: 'Review KYC', desc: 'Pending dealer verifications', icon: '🛡️', color: '#B0B8C1', page: 'kyc' },
    { title: 'Approve Products', desc: 'New listings waiting', icon: '📦', color: '#C9A84C', page: 'products' },
    { title: 'Process Payouts', desc: 'Dealer settlements due', icon: '💰', color: '#52C41A', page: 'finance' },
    { title: 'Open Tickets', desc: 'Support disputes pending', icon: '🎧', color: '#FF4D4F', page: 'support' },
  ];

  // System status items
  const systemStatus = [
    { name: 'Firebase Auth', status: 'operational', color: '#52C41A' },
    { name: 'Firestore Database', status: 'operational', color: '#52C41A' },
    { name: 'Google Auth', status: 'operational', color: '#52C41A' },
    { name: 'Admin Panel', status: 'operational', color: '#52C41A' },
    { name: 'Razorpay', status: 'not connected', color: '#FAAD14' },
    { name: 'KamSathi Logistics', status: 'not connected', color: '#FAAD14' },
  ];

  return (
    <div>

      {/* ── Welcome Banner ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1A1A1A, #2A2A1A)',
        border: '0.5px solid rgba(201,168,76,0.3)',
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#C9A84C' }}>
            🚀 KamSathi Admin Panel — Sprint 1 Complete
          </div>
          <div style={{ fontSize: 13, color: '#7A8A96', marginTop: 4 }}>
            Firebase connected · Google Auth live · Whitelist security active · Building modules now
          </div>
        </div>
        <div style={{
          background: 'rgba(82,196,26,0.15)',
          border: '0.5px solid rgba(82,196,26,0.3)',
          borderRadius: 8,
          padding: '6px 16px',
          fontSize: 12,
          color: '#52C41A',
          fontWeight: 500,
        }}>
          ● Live on localhost 
        </div>
      </div>

      {/* ── Stats Row 1 ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Total Admin Users"
            value={stats.totalUsers}
            icon={<UserOutlined style={{ color: '#C9A84C' }} />}
            color="#C9A84C"
            trend="Whitelist secured"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="KYC Pending"
            value={stats.pendingKYC}
            icon={<SafetyCertificateOutlined style={{ color: '#B0B8C1' }} />}
            color="#B0B8C1"
            trend="Module coming next"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Total Orders"
            value={stats.totalOrders}
            icon={<ShoppingOutlined style={{ color: '#52C41A' }} />}
            color="#52C41A"
            trend="Dealer app pending"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Revenue (GMV)"
            value={stats.totalRevenue}
            prefix="₹"
            icon={<DollarOutlined style={{ color: '#FAAD14' }} />}
            color="#FAAD14"
            trend="Payment integration pending"
          />
        </Col>
      </Row>

      {/* ── Stats Row 2 ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Active Deliveries"
            value={stats.activeDeliveries}
            icon={<CarOutlined style={{ color: '#1890FF' }} />}
            color="#1890FF"
            trend="Logistics app pending"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Open Tickets"
            value={stats.openTickets}
            icon={<FileTextOutlined style={{ color: '#FF4D4F' }} />}
            color="#FF4D4F"
            trend="Support module pending"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Leads Today"
            value={stats.leadsToday}
            icon={<RiseOutlined style={{ color: '#9A7A2E' }} />}
            color="#9A7A2E"
            trend="Contractor app pending"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Pending Payouts"
            value={stats.pendingPayouts}
            prefix="₹"
            icon={<ClockCircleOutlined style={{ color: '#722ED1' }} />}
            color="#722ED1"
            trend="Finance module pending"
          />
        </Col>
      </Row>

      {/* ── Bottom Row ── */}
      <Row gutter={[16, 16]}>

        {/* Quick Actions */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <span style={{ color: '#C9A84C', fontSize: 14 }}>
                ⚡ Quick Actions
              </span>
            }
            style={CARD_STYLE}
            headStyle={{
              borderBottom: '0.5px solid rgba(201,168,76,0.2)',
              background: '#1A1A1A',
            }}
            bodyStyle={{ padding: 12 }}
          >
            {quickActions.map((action, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  marginBottom: 8,
                  background: '#222222',
                  border: '0.5px solid rgba(201,168,76,0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#C9A84C'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.15)'}
              >
                <div style={{ fontSize: 24 }}>{action.icon}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#D4DCE3' }}>
                    {action.title}
                  </div>
                  <div style={{ fontSize: 11, color: '#7A8A96' }}>{action.desc}</div>
                </div>
                <div style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>→</div>
              </div>
            ))}
          </Card>
        </Col>

        {/* Recent Activity */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <span style={{ color: '#C9A84C', fontSize: 14 }}>
                📋 Recent Activity
              </span>
            }
            style={CARD_STYLE}
            headStyle={{
              borderBottom: '0.5px solid rgba(201,168,76,0.2)',
              background: '#1A1A1A',
            }}
            bodyStyle={{ padding: 12 }}
          >
            {recentActivity.map((item, i) => (
              <div
                key={item.id || i}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  marginBottom: 8,
                  background: '#222222',
                  border: '0.5px solid rgba(176,184,193,0.1)',
                }}
              >
                <div style={{ fontSize: 12, color: '#D4DCE3', fontWeight: 500 }}>
                  {item.action}
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 4,
                }}>
                  <span style={{ fontSize: 11, color: '#7A8A96' }}>
                    {item.adminEmail}
                  </span>
                  <span style={{ fontSize: 11, color: '#4A4A47' }}>
                    {item.time}
                  </span>
                </div>
              </div>
            ))}
          </Card>
        </Col>

        {/* System Status */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <span style={{ color: '#C9A84C', fontSize: 14 }}>
                🔧 System Status
              </span>
            }
            style={CARD_STYLE}
            headStyle={{
              borderBottom: '0.5px solid rgba(201,168,76,0.2)',
              background: '#1A1A1A',
            }}
            bodyStyle={{ padding: 12 }}
          >
            {systemStatus.map((item, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  borderRadius: 8,
                  marginBottom: 8,
                  background: '#222222',
                  border: '0.5px solid rgba(176,184,193,0.1)',
                }}
              >
                <span style={{ fontSize: 13, color: '#D4DCE3' }}>{item.name}</span>
                <Tag style={{
                  background: `${item.color}20`,
                  border: `0.5px solid ${item.color}50`,
                  color: item.color,
                  borderRadius: 20,
                  fontSize: 11,
                }}>
                  ● {item.status}
                </Tag>
              </div>
            ))}

            {/* Build Progress */}
            <div style={{
              marginTop: 8,
              padding: '10px 12px',
              background: 'rgba(201,168,76,0.08)',
              border: '0.5px solid rgba(201,168,76,0.2)',
              borderRadius: 8,
            }}>
              <div style={{ fontSize: 12, color: '#C9A84C', fontWeight: 500, marginBottom: 6 }}>
                Build Progress
              </div>
              {[
                { label: 'Sprint 1 — Foundation', done: true },
                { label: 'Sprint 2 — Marketplace', done: false },
                { label: 'Sprint 3 — Real Estate & B2B', done: false },
                { label: 'Sprint 4 — Scale & Revenue', done: false },
              ].map((sprint, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 11, color: sprint.done ? '#52C41A' : '#4A4A47',
                  marginBottom: 4,
                }}>
                  <span>{sprint.done ? '✅' : '⏳'}</span>
                  {sprint.label}
                </div>
              ))}
            </div>

          </Card>
        </Col>

      </Row>
    </div>
  );
}